import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { VStack } from '@chakra-ui/react';
import { useModule } from '@starberry/components';

import FormSelectField from './SelectField';
import FormHeading from './Heading';
import FormDateField from './FormDateField';
import FormDateTimeField from './FormDateTimeField';
import FormFileField from './FormFileField';
import FormSwitchField from './FormSwitchField';
import FormRadioField from './FormRadioField';
import FormInputField from './FormInputField';
import Autocomplete from './autocomplete';
import PhoneInputField from 'core/components/PhoneInputField';
import addDefaultFieldValidatorMessages from 'core/components/forms/validationMessages';
import PasswordField from 'core/components/PasswordField';
import FormPasswordField from 'core/components/forms/FormPasswordField';

const GlobalFormComponent = ({
  formFields = [],
  formStateType,
  onSubmit,
  onChange,

  inputProps,
  selectFieldProps,
  fieldsTitle,
  hideForm = false,
  submitLabel = 'Submit',
  noSubmitButton = false,

  ...rest
}) => {
  const { register, control, handleSubmit, setValue, formState } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const PrimaryButton = useModule('PrimaryButton');

  const renderFields = useMemo(() => {
    if (hideForm) {
      return null;
    }

    return formFields.map((fieldDefs) => {
      const defaultValue =
        formStateType?.[fieldDefs.name] !== undefined
          ? formStateType?.[fieldDefs.name]
          : fieldDefs?.defaultValue;

      const field = {
        ...fieldDefs,
      };

      if (
        field.basedOn &&
        !formStateType?.[field.basedOn] &&
        !field.values[formStateType?.[field.basedOn]]
      ) {
        return null;
      }

      addDefaultFieldValidatorMessages(field);

      const fieldFormProps = register(field.name, {
        ...field,
        value: defaultValue,
        disabled: false,
      });

      switch (field.type) {
        case 'text':
        case 'email':
        case 'number':
          return (
            <FormInputField
              key={field.id}
              control={control}
              field={field}
              {...fieldFormProps}
              inputProps={inputProps}
              onChange={
                onChange ? (e) => onChange(field.name, e.target.value) : fieldFormProps.onChange
              }
              defaultValue={defaultValue}
            />
          );
        case 'password':
          return (
            <FormPasswordField
              key={field.id}
              control={control}
              field={field}
              {...fieldFormProps}
              inputProps={inputProps}
              type="password"
              onChange={
                onChange ? (e) => onChange(field.name, e.target.value) : fieldFormProps.onChange
              }
              defaultValue={defaultValue}
            />
          );
        case 'tel':
          return (
            <PhoneInputField
              key={field.id}
              control={control}
              field={field}
              {...fieldFormProps}
              inputProps={inputProps}
              onChange={
                onChange ? (e) => onChange(field.name, e.target.value) : fieldFormProps.onChange
              }
              defaultValue={defaultValue}
            />
          );
        case 'autocomplete':
          return (
            <Autocomplete
              key={field.id}
              control={control}
              {...field}
              {...inputProps}
              {...fieldFormProps}
              onChange={
                onChange
                  ? (v) => onChange(field.name, v)
                  : (value) => {
                      setValue(field.name, value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }
              }
              defaultValue={defaultValue}
            />
          );
        case 'select':
          return (
            <Controller
              control={control}
              defaultValue={defaultValue}
              value={defaultValue}
              name={field.name}
              render={({ field: { onChange: _onChange, value } }) => {
                let selected = undefined;
                let _values = field.values ?? [];
                if (field.basedOn) {
                  _values = field.values[formStateType?.[field.basedOn]] ?? [];
                }
                const values = _values.map((item) => {
                  let optionValue = item;
                  let optionLabel = item;
                  if (typeof item === 'object') {
                    Object.keys(item).forEach((key) => {
                      optionValue = key;
                      optionLabel = item[key];
                    });
                  }

                  const option = { label: optionLabel, value: optionValue };
                  if (optionValue === value) {
                    selected = option;
                  }

                  return option;
                });

                return (
                  <FormSelectField
                    key={field.id}
                    {...field}
                    {...selectFieldProps}
                    values={values}
                    onChange={
                      onChange
                        ? (value) => {
                            onChange(field.name, value);
                            _onChange(value);
                          }
                        : (v) => {
                            _onChange(v);
                          }
                    }
                    defaultValue={selected}
                    value={selected}
                  />
                );
              }}
            />
          );
        case 'heading':
          return <FormHeading key={field.id} {...field} {...fieldsTitle} />;
        case 'date':
          return (
            <FormDateField
              key={field.id}
              {...field}
              {...inputProps}
              {...fieldFormProps}
              onChange={onChange ? (value) => onChange(field.name, value) : fieldFormProps.onChange}
              // defaultValue={defaultValue}
            />
          );
        case 'datetime':
          return (
            <FormDateTimeField
              key={field.id}
              {...field}
              {...inputProps}
              {...fieldFormProps}
              onChange={onChange ? (value) => onChange(field.name, value) : fieldFormProps.onChange}
              // defaultValue={defaultValue}
            />
          );
        case 'file':
          return (
            <FormFileField
              key={field.id}
              {...field}
              {...inputProps}
              {...fieldFormProps}
              onChange={onChange ? (value) => onChange(field.name, value) : fieldFormProps.onChange}
              // defaultValue={defaultValue}
            />
          );
        case 'switch':
          return (
            <Controller
              control={control}
              defaultValue={defaultValue}
              name={field.id}
              render={({ field: { onChange: _onChange, onBlur, value, ref } }) => (
                <FormSwitchField
                  key={field.id}
                  {...field}
                  {...inputProps}
                  {...fieldFormProps}
                  onBlur={onBlur}
                  onChange={(e) => {
                    onChange(field.id, e.target.checked);
                    setValue(field.name, value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  isChecked={value}
                  inputRef={ref}
                />
              )}
            />
          );
        case 'radio':
          return (
            <FormRadioField
              key={field.id}
              {...field}
              {...inputProps}
              {...fieldFormProps}
              onChange={onChange ? (value) => onChange(field.name, value) : fieldFormProps.onChange}
              defaultChecked={Boolean(defaultValue)}
            />
          );
        default:
          return <></>;
      }
    });
  }, [
    formFields,
    control,
    register,
    hideForm,
    formStateType,
    fieldsTitle,
    inputProps,
    selectFieldProps,
    onChange,
    setValue,
  ]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={7} align="flex-start" {...rest}>
        {renderFields}
        {!noSubmitButton && !hideForm && (
          <PrimaryButton type="submit" disabled={!formState.isValid}>
            {submitLabel}
          </PrimaryButton>
        )}
      </VStack>
    </form>
  );
};

export default GlobalFormComponent;
